import React, { useState, useCallback, useEffect, useMemo, Suspense, lazy } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
    Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton,
    ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem,
    Avatar, useMediaQuery, useTheme, Collapse, Badge, Tooltip,
} from '@mui/material';
import {
    Menu as MenuIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
    Dashboard as DashboardIcon, People as PeopleIcon,
    AccountBalance as AccountBalanceIcon, Receipt as ReceiptIcon,
    AttachMoney as AttachMoneyIcon, Assessment as AssessmentIcon,
    Payment as PaymentIcon, MoneyOff as MoneyOffIcon,
    Inventory as InventoryIcon, Category as CategoryIcon,
    LocalShipping as LocalShippingIcon, ShoppingCart as ShoppingCartIcon,
    Logout,
    ExpandLess, ExpandMore, Store as StoreIcon, TrendingUp as TrendingUpIcon,
    Settings as SettingsIcon, Business as BusinessIcon,
    Assignment as AssignmentIcon, Description as DescriptionIcon, Person as PersonIcon,
    AccountTree as AccountTreeIcon, Security as SecurityIcon,
    History as HistoryIcon, Factory as FactoryIcon,
    Warning as AlertTriangleIcon, Map as MapIcon,
    EmojiEvents as EmojiEventsIcon,
    Hub as CommandCenterIcon,
    Route as RouteIcon, EventAvailable as LeaveIcon,
    Groups as GroupsIcon, Public as PublicIcon,
    Policy as PolicyIcon,
    CloudOff as CloudOffIcon,
    Storage as StorageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { formatUserLabel, formatUserMeta } from '../../utils/formatUserLabel';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import TrialBanner from '../common/TrialBanner';
import NotificationBell from '../notifications/NotificationBell';
import WorkingDistributorChip from './WorkingDistributorChip';
import GlobalSearchBar from '../search/GlobalSearchBar';
import { useOffline } from '../../context/OfflineContext';
import { useWorkdayGate } from '../../workday/WorkdayGateContext';
import BrandLogo from '../brand/BrandLogo';
import axios from '../../utils/axios';
import { filterNavItemForRole, getWorkspaceNavPrefix, getWorkspaceSidebarItems } from '../../config/navigation';
import { canAccessActivityLogs } from '../../config/roles';
import { SHOW_MANUFACTURING_UI } from '../../config/features';

const AISupportChat = lazy(() => import('../ai/AISupportChat'));

/** Map path to RBAC module for granular permission checks */
const pathToModule = {
    '/dashboard': 'dashboard',
    '/customer-invoices': 'invoices',
    '/drafts': 'drafts',
    '/draft-invoices': 'drafts',
    '/payment-receipts': 'paymentsReceipts',
    '/claims': 'claims',
    '/draft-receipts': 'drafts',
    '/exceptions/policy-settings': 'drafts',
    '/customers': 'customers',
    '/customer-attributes': 'customers',
    '/price-lists': 'customers',
    '/inventory/products': 'products',
    '/inventory/warehouses': 'products',
    '/inventory/stock-transfer': 'products',
    '/inventory/categories': 'products',
    '/inventory/brands': 'products',
    '/inventory/attributes': 'products',
    '/inventory/suppliers': 'products',
    '/inventory/stock-movements': 'products',
    '/reports': 'reports',
    '/users': 'users',
    '/territories': 'users',
    '/network': 'distributionNetwork',
    '/purchasing': 'products',
    '/purchasing/create': 'products',
    '/supplier-payments': 'paymentsReceipts',
    '/cheque-register': 'paymentsReceipts',
    '/accounting': 'bank',
    '/operations/bank': 'bank',
    '/operations/daily-closing': 'bank',
    '/operations/closing-history': 'bank',
    '/operations/reconciliation': 'bank',
    '/operations/exceptions': 'drafts',
    '/payables': 'products',
    '/expenses': 'expenses',
    '/mfg/raw-materials': 'manufacturing',
    '/mfg/finished-goods': 'manufacturing',
    '/mfg/planning': 'manufacturing',
    '/mfg/cost-optimizer': 'manufacturing',
    '/mfg/bom': 'manufacturing',
    '/mfg/production-order': 'manufacturing',
    '/mfg/batch': 'manufacturing',
    '/mfg/closing': 'manufacturing',
    '/mfg/fg-inventory': 'manufacturing',
    '/mfg/rm-inventory': 'manufacturing',
    '/mfg/wip-inventory': 'manufacturing',
    '/mfg/audit-log': 'manufacturing',
    '/mfg/rm-receiving': 'manufacturing',
    '/mfg/quality-control': 'manufacturing',
    '/mfg/analytics': 'manufacturing',
    '/mfg/ai-pricing': 'manufacturing',
    '/mfg/cost-settings': 'manufacturing',
    '/tasks': 'tasks',
    '/payroll/runs': 'payroll',
    '/payroll/employees': 'payroll',
    '/payroll': 'payroll',
    '/incentives': 'incentives',
    '/billing': 'billing',
    '/billing/invoices': 'billing',
};

const DRAWER_WIDTH = 272;
const DRAWER_RAIL = 76;
const SIDEBAR_STORAGE_KEY = 'tauris.sidebarExpanded.v3';

// ── Theme colors ──
const C = {
    primary: '#09b8a9', primaryDark: '#078d82', secondary: '#383b50',
    bg: '#fcfefe', sidebar: '#FFFFFF', sidebarHeader: '#1c1f2e',
    active: '#e6f7f5', activeBorder: '#09b8a9', hover: '#f0faf9',
    text: '#383b50', textSec: '#8a93a6', border: '#e5edec',
    contentBg: '#f5f8f7',
};

/** Resolve workspace iconKey → MUI icon element */
const NAV_ICON_MAP = {
    dashboard: DashboardIcon,
    myDay: AssignmentIcon,
    tasks: AssignmentIcon,
    targets: TrendingUpIcon,
    route: RouteIcon,
    field: GroupsIcon,
    outstanding: AccountBalanceIcon,
    geo: MapIcon,
    exceptions: AlertTriangleIcon,
    incentives: EmojiEventsIcon,
    performance: AssessmentIcon,
    recovery: PaymentIcon,
    approvals: DescriptionIcon,
    employees: PeopleIcon,
    attendance: AssignmentIcon,
    policy: PolicyIcon,
    leave: LeaveIcon,
    payroll: AttachMoneyIcon,
    portal: PersonIcon,
    distributors: StoreIcon,
    regions: AccountTreeIcon,
    national: PublicIcon,
    customers: PeopleIcon,
    invoice: DescriptionIcon,
    receipt: ReceiptIcon,
    catalog: InventoryIcon,
    inventory: InventoryIcon,
    receiving: LocalShippingIcon,
    dispatch: LocalShippingIcon,
    adjust: CategoryIcon,
};

function resolveNavIcon(iconKey, fallback = DashboardIcon) {
    const Comp = (iconKey && NAV_ICON_MAP[iconKey]) || fallback;
    return <Comp />;
}

/**
 * Navigation structure — organized logically:
 * Dashboard → Sales → Purchases → Inventory → Reports → Settings
 */
const getNavigationMenu = (userRole, can) => {
    if (userRole === 'SaaSAdmin') {
        return [
            {
                category: 'Tauris Admin',
                icon: <SettingsIcon />,
                items: [
                    { text: 'Dashboard', path: '/admin/dashboard', icon: <DashboardIcon />, roles: ['SaaSAdmin'] },
                    { text: 'My Profile', path: '/settings/profile', icon: <PersonIcon />, roles: ['SaaSAdmin'] },
                    { text: 'Users', path: '/admin/users', icon: <PeopleIcon />, roles: ['SaaSAdmin'] },
                    { text: 'Companies', path: '/admin/companies', icon: <BusinessIcon />, roles: ['SaaSAdmin'] },
                    { text: 'Distribution Network', path: '/admin/distribution-network', icon: <AccountTreeIcon />, roles: ['SaaSAdmin'] },
                    { text: 'Roles & Permissions', path: '/admin/roles', icon: <SecurityIcon />, roles: ['SaaSAdmin'] },
                    { text: 'System Settings', path: '/admin/settings', icon: <SettingsIcon />, roles: ['SaaSAdmin'] },
                    { text: 'Demo Data', path: '/admin/demo-data', icon: <StorageIcon />, roles: ['SaaSAdmin'] },
                    { text: 'Activity Logs', path: '/admin/activity-logs', icon: <HistoryIcon />, roles: ['SaaSAdmin'] },
                ],
            },
        ];
    }

    const menu = [];
    const ALL_ADMIN = ['Owner', 'CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin'];
    const FINANCE = [...ALL_ADMIN, 'Accountant', 'AccountsStaff'];
    const INVENTORY = [...ALL_ADMIN, 'StoreStaff', 'WarehouseStaff', 'AccountsStaff', 'Accountant'];
    const SALES_HIERARCHY = ['NSM', 'RSM', 'ZSM', 'ASM'];
    const REPORT_VIEWERS = [...ALL_ADMIN, 'Accountant', 'AccountsStaff', ...SALES_HIERARCHY, 'HRManager'];

    // Single source of truth: the same resolver the API enforces with, so the sidebar
    // never offers a page that would 403.
    const hasAccess = (module, action = 'view') => !module || !can || can(module, action);

    // ── ROLE WORKSPACE (dedicated sidebar section for staff / hierarchy roles) ──
    const workspaceLink = getWorkspaceNavPrefix(userRole);
    const workspaceSidebarItems = getWorkspaceSidebarItems(userRole);
    const workspacePaths = new Set(workspaceSidebarItems.map((item) => item.path));

    const filterNavItem = (item, { skipWorkspaceDedup = false } = {}) => {
        if (!filterNavItemForRole(userRole, item)) return false;
        if (!skipWorkspaceDedup && !item.workspaceNav && workspacePaths.has(item.path)) return false;
        const roleAllowed = item.roles ? item.roles.includes(userRole) : true;
        const isPlatformItem = String(item.path || '').startsWith('/admin/');
        const resolvedModule = item.module || pathToModule[item.path] || null;
        const requiredAction = item.action || 'view';
        if (isPlatformItem) return roleAllowed && hasAccess(resolvedModule, requiredAction);
        if (resolvedModule) return roleAllowed && hasAccess(resolvedModule, requiredAction);
        return roleAllowed;
    };

    if (workspaceLink) {
        const workspaceItems = [
            {
                text: 'Dashboard',
                path: workspaceLink.path,
                icon: <DashboardIcon />,
                module: null,
                roles: [userRole],
                workspaceNav: true,
            },
            ...workspaceSidebarItems.map((item) => ({
                text: item.text,
                path: item.path,
                icon: resolveNavIcon(item.iconKey),
                module: item.module || null,
                action: item.action || 'view',
                roles: [userRole],
                workspaceNav: true,
                badge: item.badge || null,
            })),
        ].filter((item) => filterNavItem(item, { skipWorkspaceDedup: true }));

        if (workspaceItems.length > 0) {
            menu.push({
                category: workspaceLink.label,
                icon: <DashboardIcon />,
                isWorkspaceCategory: true,
                items: workspaceItems,
            });
        }
    }

    // ── DASHBOARD (admin ERP overview — workspace roles use Workspace section) ──
    const dashboardItems = [
        { text: 'Overview', path: '/dashboard', icon: <DashboardIcon />, module: 'dashboard', roles: [...ALL_ADMIN, 'Cashier', 'StoreStaff'] },
        { text: 'Command Center', path: '/command-center', icon: <CommandCenterIcon />, roles: ALL_ADMIN },
        { text: 'Tasks', path: '/tasks', icon: <AssignmentIcon />, module: 'tasks', roles: [...ALL_ADMIN, 'StoreStaff', 'HRManager', ...SALES_HIERARCHY], badge: 'pendingTasks' },
        { text: 'Offline Queue', path: '/offline-queue', icon: <CloudOffIcon />, roles: [...ALL_ADMIN, 'Salesman', 'AccountsStaff', 'Accountant', 'ASM', 'ZSM', 'RSM', 'NSM', 'WarehouseStaff', 'Cashier', 'StoreStaff'] },
    ];
    const dashboardFiltered = dashboardItems.filter((item) => filterNavItem(item));
    if (dashboardFiltered.length > 0 && !workspaceLink) {
        menu.push({
            category: 'Dashboard',
            icon: <DashboardIcon />,
            items: dashboardFiltered,
        });
    }

    // ── SALES (drafts live on Invoices / Receipts tabs — badge on parent) ──
    menu.push({
        category: 'Sales', icon: <ShoppingCartIcon />,
        items: [
            { text: 'Customers', path: '/customers', icon: <PeopleIcon />, module: 'customers', roles: [...FINANCE, 'Salesman'] },
            { text: 'Invoices', path: '/customer-invoices', icon: <DescriptionIcon />, module: 'invoices', roles: FINANCE, badge: 'draftInvoices' },
            { text: 'Receipts', path: '/payment-receipts', icon: <ReceiptIcon />, module: 'paymentsReceipts', roles: FINANCE, badge: 'draftReceipts' },
            // Unified /drafts is for field roles only — admins/KPO use Invoices/Receipts Drafts tabs
            { text: 'Drafts', path: '/drafts', icon: <DescriptionIcon />, module: 'drafts', roles: ['Salesman', ...SALES_HIERARCHY] },
            { text: 'Claims', path: '/claims', icon: <AssessmentIcon />, module: 'claims', roles: FINANCE },
        ].filter((i) => (!i.roles || i.roles.includes(userRole)) && hasAccess(i.module, i.action || 'view')),
    });

    // ── PURCHASING ──
    menu.push({
        category: 'Purchasing', icon: <LocalShippingIcon />,
        items: [
            { text: 'Purchase Bills', path: '/purchasing', icon: <ShoppingCartIcon />, module: 'products', roles: FINANCE, badge: 'draftPurchases' },
            { text: 'Supplier Payments', path: '/supplier-payments', icon: <PaymentIcon />, module: 'paymentsReceipts', roles: FINANCE, badge: 'draftPayments' },
            { text: 'Suppliers', path: '/payables', icon: <StoreIcon />, module: 'products', roles: FINANCE },
        ].filter((i) => (!i.roles || i.roles.includes(userRole)) && hasAccess(i.module, i.action || 'view')),
    });

    // ── INVENTORY ──
    menu.push({
        category: 'Inventory', icon: <InventoryIcon />,
        items: [
            { text: 'Products', path: '/inventory/products', icon: <InventoryIcon />, module: 'products', roles: INVENTORY },
            { text: 'Warehouses', path: '/inventory/warehouses', icon: <StoreIcon />, module: 'products', roles: INVENTORY },
            { text: 'Stock Transfer', path: '/inventory/stock-transfer', icon: <LocalShippingIcon />, module: 'products', roles: INVENTORY },
            { text: 'Stock Adjustment', path: '/inventory/stock-adjustment', icon: <LocalShippingIcon />, module: 'products', roles: INVENTORY },
            { text: 'Stock Movement', path: '/inventory/stock-movements', icon: <InventoryIcon />, module: 'products', roles: INVENTORY },
        ].filter((i) => (!i.roles || i.roles.includes(userRole)) && hasAccess(i.module, i.action || 'view')),
    });

    // ── OPERATIONS (day-end / exceptions — Admin & KPO; My Day stays under Workspace) ──
    const operationsItems = [
        { text: 'Daily Closing', path: '/operations/daily-closing', icon: <AssignmentIcon />, module: 'bank', roles: [...FINANCE] },
        { text: 'Reconciliation', path: '/operations/reconciliation', icon: <AccountBalanceIcon />, module: 'bank', roles: [...FINANCE] },
        { text: 'KPO Exceptions', path: '/operations/exceptions', icon: <AlertTriangleIcon />, module: 'drafts', roles: [...FINANCE] },
        { text: 'Exception Center', path: '/exceptions', icon: <AlertTriangleIcon />, roles: [...FINANCE, 'HRManager', 'WarehouseStaff', ...SALES_HIERARCHY, 'Salesman'] },
    ].filter((i) => (!i.roles || i.roles.includes(userRole)) && (!i.module || hasAccess(i.module, i.action || 'view')));
    if (operationsItems.length) {
        menu.push({ category: 'Operations', icon: <AssignmentIcon />, items: operationsItems });
    }

    // ── MANUFACTURING (Company only) — hidden until SHOW_MANUFACTURING_UI ──
    if (SHOW_MANUFACTURING_UI && ['Owner', 'CompanyAdmin'].includes(userRole)) {
        menu.push({
            category: 'Manufacturing', icon: <FactoryIcon />,
            items: [
                { text: 'Bill of Materials', path: '/mfg/bom', icon: <AssignmentIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'RM Receiving', path: '/mfg/rm-receiving', icon: <LocalShippingIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Quality Control', path: '/mfg/quality-control', icon: <AssignmentIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Production Planning', path: '/mfg/planning', icon: <AssignmentIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Production Orders', path: '/mfg/production-order', icon: <FactoryIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Batch Production', path: '/mfg/batch', icon: <FactoryIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Production Closing', path: '/mfg/closing', icon: <AssignmentIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'FG Inventory', path: '/mfg/fg-inventory', icon: <InventoryIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'RM Inventory', path: '/mfg/rm-inventory', icon: <InventoryIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'WIP Inventory', path: '/mfg/wip-inventory', icon: <InventoryIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Cost of Goods (COGS)', path: '/mfg/ai-pricing', icon: <AttachMoneyIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Cost Optimizer', path: '/mfg/cost-optimizer', icon: <TrendingUpIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Cost Settings', path: '/mfg/cost-settings', icon: <SettingsIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Production Analytics', path: '/mfg/analytics', icon: <TrendingUpIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
                { text: 'Audit Trail', path: '/mfg/audit-log', icon: <DescriptionIcon />, module: 'manufacturing', roles: ['Owner', 'CompanyAdmin'] },
            ].filter((i) => hasAccess(i.module, i.action || 'view')),
        });
    }

    // ── FINANCE (GL / books / bank) ──
    menu.push({
        category: 'Finance', icon: <AccountBalanceIcon />,
        items: [
            { text: 'Cash & Bank', path: '/accounting', icon: <PaymentIcon />, module: 'bank', roles: [...ALL_ADMIN, 'Accountant', 'AccountsStaff'] },
            { text: 'Bank Accounts', path: '/operations/bank', icon: <PaymentIcon />, module: 'bank', roles: [...FINANCE] },
            { text: 'Cheque Register', path: '/cheque-register', icon: <AccountBalanceIcon />, module: 'paymentsReceipts', roles: FINANCE },
            { text: 'Expenses', path: '/expenses', icon: <MoneyOffIcon />, module: 'expenses', roles: FINANCE },
            { text: 'Payroll Runs', path: '/payroll/runs', icon: <AttachMoneyIcon />, module: 'payroll', roles: [...ALL_ADMIN, 'Accountant', 'AccountsStaff'] },
            { text: 'Payroll Employees', path: '/payroll/employees', icon: <PeopleIcon />, module: 'payroll', roles: [...ALL_ADMIN, 'Accountant', 'AccountsStaff'] },
            { text: 'Subscription Billing', path: '/billing', icon: <ReceiptIcon />, module: 'billing', roles: FINANCE },
        ].filter((i) => (!i.roles || i.roles.includes(userRole)) && hasAccess(i.module, i.action || 'view')),
    });

    // ── TARGETS & INCENTIVES ──
    menu.push({
        category: 'Targets & Incentives', icon: <EmojiEventsIcon />,
        items: [
            { text: 'Incentive Hub', path: '/incentives', icon: <EmojiEventsIcon />, module: 'incentives', roles: ['Salesman', 'ASM'] },
            { text: 'My Incentives', path: '/incentives/my', icon: <TrendingUpIcon />, module: 'incentives', roles: ['Salesman', 'ASM'] },
            { text: 'Targets & Incentives', path: '/team-targets-incentives', icon: <AssessmentIcon />, module: 'incentives', action: 'view', roles: [...ALL_ADMIN, 'NSM', 'RSM', 'ZSM', 'ASM'] },
            { text: 'Approvals', path: '/incentives/approvals', icon: <DescriptionIcon />, module: 'incentives', action: 'approve', roles: [...ALL_ADMIN, 'HRManager', 'NSM', 'RSM', 'ZSM'] },
            { text: 'Incentive Reports', path: '/incentives/reports', icon: <AssessmentIcon />, module: 'incentives', roles: [...ALL_ADMIN, 'HRManager', 'NSM', 'RSM', 'ZSM', 'ASM'] },
        ].filter((i) => (!i.roles || i.roles.includes(userRole)) && hasAccess(i.module, i.action || 'view')),
    });

    // ── REPORTS (hub only — all reports open from /reports) ──
    const reportHubItems = [
        { text: 'Reports Hub', path: '/reports', icon: <AssessmentIcon />, module: 'reports', roles: REPORT_VIEWERS },
    ].filter((i) => (!i.roles || i.roles.includes(userRole)) && hasAccess(i.module, i.action || 'view'));
    if (reportHubItems.length) {
        menu.push({ category: 'Reports', icon: <AssessmentIcon />, items: reportHubItems });
    }

    // ── NETWORK (distribution hierarchy — Admin) ──
    const networkItems = [
        { text: 'Distribution Network', path: '/network', icon: <AccountTreeIcon />, module: 'distributionNetwork', roles: ['Owner', 'CompanyAdmin', 'SoleDistributorAdmin'] },
        { text: 'Territories', path: '/territories', icon: <MapIcon />, module: 'users', roles: ['CompanyAdmin', 'Owner'] },
    ].filter((i) => hasAccess(i.module, i.action || 'view'));
    if (networkItems.length) {
        menu.push({ category: 'Network', icon: <BusinessIcon />, items: networkItems });
    }

    // ── SETTINGS ──
    menu.push({
        category: 'Settings', icon: <SettingsIcon />,
        items: [
            { text: 'My Profile', path: '/settings/profile', icon: <PersonIcon />, roles: ALL_ADMIN },
            { text: 'Users & Roles', path: '/users', icon: <PeopleIcon />, module: 'users', roles: ALL_ADMIN },
            { text: 'Role Management', path: '/users/roles', icon: <PeopleIcon />, module: 'users', roles: ALL_ADMIN },
            { text: 'Document Headers', path: '/settings/document-print', icon: <DescriptionIcon />, roles: ALL_ADMIN },
            { text: 'Attendance Rules', path: '/hr/attendance-policy', icon: <PolicyIcon />, roles: [...ALL_ADMIN, 'HRManager'] },
            ...(canAccessActivityLogs(userRole) ? [{ text: 'Activity Log', path: '/activity-logs', icon: <HistoryIcon />, roles: ['CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin', 'Owner'] }] : []),
        ].filter((i) => hasAccess(i.module, i.action || 'view')),
    });

    // Hide ERP categories that don't belong on dedicated workspace sidebars.
    // KPO / Accountant keep the full operational ERP tree (minus Settings / Network).
    // Hierarchy / HR: workspace-only — hide duplicate ERP Incentives, Reports, General.
    const REPORT_CATEGORY_NAMES = ['Reports', 'Product Reports', 'Customer Reports', 'Financial Reports', 'Inventory Reports'];
    const HIDDEN_ERP_CATEGORIES_BY_ROLE = {
        ASM: ['Sales', ...REPORT_CATEGORY_NAMES, 'Incentives', 'Targets & Incentives', 'Purchasing', 'Purchasing & Payables', 'Purchases', 'Inventory', 'Operations', 'Finance', 'Bank', 'Network', 'Settings', 'General'],
        ZSM: ['Sales', ...REPORT_CATEGORY_NAMES, 'Incentives', 'Targets & Incentives', 'Purchasing', 'Purchasing & Payables', 'Purchases', 'Inventory', 'Operations', 'Finance', 'Bank', 'Network', 'Settings', 'General'],
        RSM: ['Sales', ...REPORT_CATEGORY_NAMES, 'Incentives', 'Targets & Incentives', 'Purchasing', 'Purchasing & Payables', 'Purchases', 'Inventory', 'Operations', 'Finance', 'Bank', 'Network', 'Settings', 'General'],
        NSM: ['Sales', ...REPORT_CATEGORY_NAMES, 'Incentives', 'Targets & Incentives', 'Purchasing', 'Purchasing & Payables', 'Purchases', 'Inventory', 'Operations', 'Finance', 'Bank', 'Network', 'Settings', 'General'],
        Salesman: ['Sales', ...REPORT_CATEGORY_NAMES, 'Incentives', 'Targets & Incentives', 'Purchasing', 'Purchasing & Payables', 'Purchases', 'Inventory', 'Operations', 'Finance', 'Bank', 'Network', 'Settings', 'General'],
        WarehouseStaff: ['Sales', 'Purchasing', 'Purchasing & Payables', 'Purchases', 'Operations', 'Finance', 'Bank', ...REPORT_CATEGORY_NAMES, 'Incentives', 'Targets & Incentives', 'Network', 'Settings', 'General'],
        HRManager: ['Sales', 'Purchasing', 'Purchasing & Payables', 'Purchases', 'Inventory', 'Operations', 'Finance', 'Bank', 'Manufacturing', 'Network', 'Settings', ...REPORT_CATEGORY_NAMES, 'Incentives', 'Targets & Incentives', 'General'],
        AccountsStaff: ['Settings', 'Network', 'Targets & Incentives'],
        Accountant: ['Settings', 'Network', 'Targets & Incentives'],
    };
    const hiddenCategories = new Set(HIDDEN_ERP_CATEGORIES_BY_ROLE[userRole] || []);

    return menu
        .map((cat) => ({
            ...cat,
            items: cat.items.filter((item) => filterNavItem(item, { skipWorkspaceDedup: cat.isWorkspaceCategory })),
        }))
        .filter((cat) => cat.items.length > 0)
        .filter((cat) => cat.isWorkspaceCategory || !hiddenCategories.has(cat.category));
};

const DashboardLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [desktopExpanded, setDesktopExpanded] = useState(() => {
        try {
            // Sidebar chrome stays expanded (icons + labels) unless user collapses the rail.
            return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== '0';
        } catch {
            return true;
        }
    });
    const [anchorEl, setAnchorEl] = useState(null);
    /** Category accordion: true = open, false = closed. Default = collapsed (unless route is inside). */
    const [expandedCategories, setExpandedCategories] = useState({});
    /** Collapsed rail: flyout menu for a main section icon */
    const [railFlyout, setRailFlyout] = useState(null); // { category, anchorEl }
    const [draftInvoicesBadge, setDraftInvoicesBadge] = useState(0);
    const [draftReceiptsBadge, setDraftReceiptsBadge] = useState(0);
    const [pendingTasksBadge, setPendingTasksBadge] = useState(0);
    const { user, business, company, distributor, logout, can } = useAuth();
    const { isOnline, pendingCount, failedCount, isSyncing, storageWarning } = useOffline();
    const { gated, blocked, gate } = useWorkdayGate();
    const navigate = useNavigate();
    const workdayBlocksAppApis = gated && blocked;
    const pendingClosing = gate?.pendingDailyClosing;

    useEffect(() => {
        try {
            localStorage.setItem(SIDEBAR_STORAGE_KEY, desktopExpanded ? '1' : '0');
        } catch {
            /* ignore */
        }
    }, [desktopExpanded]);

    const fetchDraftBadge = useCallback(() => {
        const approverRoles = ['Salesman', 'Owner', 'CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin', 'Accountant', 'AccountsStaff', 'ASM', 'ZSM', 'RSM', 'NSM'];
        if (!user?.id || !approverRoles.includes(user?.role) || workdayBlocksAppApis) return;
        axios.get('/draft-invoices/badge').then((r) => {
            if (r.data?.success && typeof r.data.data?.count === 'number') setDraftInvoicesBadge(r.data.data.count);
        }).catch(() => { /* badge fetch non-critical */ });
    }, [user?.id, user?.role, workdayBlocksAppApis]);
    const fetchDraftReceiptsBadge = useCallback(() => {
        const approverRoles = ['Salesman', 'Owner', 'CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin', 'Accountant', 'AccountsStaff', 'ASM', 'ZSM', 'RSM', 'NSM'];
        if (!user?.id || !approverRoles.includes(user?.role) || workdayBlocksAppApis) return;
        axios.get('/draft-receipts/badge').then((r) => {
            if (r.data?.success && typeof r.data.data?.count === 'number') setDraftReceiptsBadge(r.data.data.count);
        }).catch(() => { /* badge fetch non-critical */ });
    }, [user?.id, user?.role, workdayBlocksAppApis]);
    const fetchPendingTasksBadge = useCallback(() => {
        if (!user?.id || workdayBlocksAppApis || !can?.('tasks', 'view')) return;
        axios.get('/tasks/badge').then((r) => {
            const total = r.data?.data?.total;
            if (r.data?.success && typeof total === 'number') setPendingTasksBadge(total);
        }).catch(() => { /* badge fetch non-critical */ });
    }, [user?.id, workdayBlocksAppApis, can]);

    useEffect(() => { fetchDraftBadge(); }, [fetchDraftBadge]);
    useEffect(() => {
        const onInvoices =
            location.pathname.startsWith('/draft-invoices')
            || location.pathname.startsWith('/drafts')
            || location.pathname.startsWith('/customer-invoices');
        if (!onInvoices) return undefined;
        let cancelled = false;
        (async () => {
            try {
                // Opening Invoices clears the "unseen drafts" badge for approvers
                await axios.post('/draft-invoices/mark-seen');
            } catch {
                /* non-critical */
            }
            if (!cancelled) fetchDraftBadge();
        })();
        return () => { cancelled = true; };
    }, [location.pathname, location.search, fetchDraftBadge]);
    useEffect(() => { fetchDraftReceiptsBadge(); }, [fetchDraftReceiptsBadge]);
    useEffect(() => {
        const onReceipts =
            location.pathname.startsWith('/draft-receipts')
            || location.pathname.startsWith('/payment-receipts');
        if (!onReceipts) return undefined;
        let cancelled = false;
        (async () => {
            try {
                await axios.post('/draft-receipts/mark-seen');
            } catch {
                /* non-critical */
            }
            if (!cancelled) fetchDraftReceiptsBadge();
        })();
        return () => { cancelled = true; };
    }, [location.pathname, location.search, fetchDraftReceiptsBadge]);
    useEffect(() => { fetchPendingTasksBadge(); }, [fetchPendingTasksBadge]);
    useEffect(() => {
        if (location.pathname.startsWith('/tasks')) fetchPendingTasksBadge();
    }, [location.pathname, fetchPendingTasksBadge]);
    useEffect(() => {
        document.title = 'Tauris';
    }, []);

    const navigationMenu = useMemo(() => getNavigationMenu(user?.role, can), [user?.role, can]);
    const railMode = !isMobile && !desktopExpanded;
    const currentDrawerWidth = isMobile ? 0 : (desktopExpanded ? DRAWER_WIDTH : DRAWER_RAIL);

    const handleDrawerToggle = () => {
        setRailFlyout(null);
        if (isMobile) setMobileOpen((o) => !o);
        else setDesktopExpanded((o) => !o);
    };

    useEffect(() => {
        setRailFlyout(null);
    }, [location.pathname, location.search, desktopExpanded]);

    const closeMobileDrawer = () => {
        if (isMobile) setMobileOpen(false);
    };

    const isCategoryExpanded = useCallback((categoryName, hasActiveItem) => {
        const explicit = expandedCategories[categoryName];
        if (explicit === true) return true;
        if (explicit === false) return false;
        // Default: sections stay collapsed; only auto-open the one containing the current page
        return Boolean(hasActiveItem);
    }, [expandedCategories]);

    const handleCategoryToggle = (categoryName, hasActiveItem) => {
        if (railMode) return;
        setExpandedCategories((prev) => {
            const explicit = prev[categoryName];
            const currentlyOpen = explicit === true || (explicit !== false && hasActiveItem);
            return { ...prev, [categoryName]: !currentlyOpen };
        });
    };

    const isItemActive = (rawPath) => {
        const path = String(rawPath || '').split('?')[0];
        const search = String(rawPath || '').includes('?')
            ? `?${String(rawPath).split('?')[1]}`
            : '';
        if (path === '/dashboard') return location.pathname === '/dashboard';
        if (path === '/operations/dashboard' || path === '/operations') {
            return location.pathname === '/operations' || location.pathname === '/operations/dashboard';
        }
        if (path === '/drafts') {
            return location.pathname === '/drafts'
                || location.pathname.startsWith('/draft-invoices')
                || location.pathname.startsWith('/draft-receipts')
                || location.pathname.startsWith('/draft-purchases');
        }
        if (path === '/customer-invoices') {
            if (search.includes('tab=drafts')) {
                return (location.pathname === '/customer-invoices' && location.search.includes('tab=drafts'))
                    || location.pathname.startsWith('/draft-invoices')
                    || location.pathname === '/drafts';
            }
            return location.pathname === '/customer-invoices' || location.pathname.startsWith('/draft-invoices');
        }
        if (path === '/payment-receipts') {
            if (search.includes('tab=drafts')) {
                return (location.pathname === '/payment-receipts' && location.search.includes('tab=drafts'))
                    || location.pathname.startsWith('/draft-receipts');
            }
            return location.pathname === '/payment-receipts' || location.pathname.startsWith('/draft-receipts');
        }
        if (path === '/purchasing') {
            return location.pathname === '/purchasing' || (location.pathname.startsWith('/purchasing/') && !location.pathname.includes('create'));
        }
        if (path === '/supplier-payments') {
            return location.pathname === '/supplier-payments' || location.pathname.startsWith('/draft-payments');
        }
        if (path.startsWith('/payables')) {
            return location.pathname === '/payables' || location.pathname.startsWith('/payables');
        }
        if (path === '/accounting') return location.pathname === path || location.pathname.startsWith(`${path}/`);
        if (path === '/operations/bank') return location.pathname === '/operations/bank';
        if (path === '/reports') return location.pathname === path || location.pathname.startsWith(`${path}/`);
        if (path === '/users') return location.pathname === '/users';
        if (path === '/network') return location.pathname === '/network' || location.pathname.startsWith('/network/');
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    const navBadgeCount = (item) => {
        if (item.badge === 'draftInvoices') return draftInvoicesBadge;
        if (item.badge === 'draftReceipts') return draftReceiptsBadge;
        if (item.badge === 'pendingTasks') return pendingTasksBadge;
        return 0;
    };

    const renderNavLabel = (item) => {
        const count = navBadgeCount(item);
        if (!count) return item.text;
        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    maxWidth: '100%',
                    verticalAlign: 'middle',
                }}
            >
                <Box
                    component="span"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    {item.text}
                </Box>
                <Box
                    component="span"
                    sx={{
                        flexShrink: 0,
                        minWidth: 18,
                        height: 18,
                        px: 0.6,
                        borderRadius: 999,
                        bgcolor: 'error.main',
                        color: '#fff',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        lineHeight: '18px',
                        textAlign: 'center',
                    }}
                >
                    {count > 99 ? '99+' : count}
                </Box>
            </Box>
        );
    };

    const renderNavItem = (item, { dense = false } = {}) => {
        const isActive = isItemActive(item.path);
        const button = (
            <ListItemButton
                component={RouterLink}
                to={item.path}
                onClick={closeMobileDrawer}
                aria-current={isActive ? 'page' : undefined}
                aria-label={item.text}
                sx={{
                    borderRadius: 1.5,
                    py: railMode ? 1.1 : dense ? 0.55 : 0.7,
                    px: railMode ? 1 : 1.25,
                    minHeight: railMode ? 44 : undefined,
                    justifyContent: railMode ? 'center' : 'flex-start',
                    bgcolor: isActive ? C.active : 'transparent',
                    borderLeft: railMode ? 'none' : (isActive ? `3px solid ${C.activeBorder}` : '3px solid transparent'),
                    color: isActive ? C.primary : C.text,
                    textDecoration: 'none',
                    transition: 'background-color 160ms ease, color 160ms ease',
                    '&:hover': { bgcolor: C.hover },
                }}
            >
                <ListItemIcon
                    sx={{
                        color: isActive ? C.primary : C.textSec,
                        minWidth: railMode ? 0 : 34,
                        mr: railMode ? 0 : 0.5,
                        justifyContent: 'center',
                    }}
                >
                    <Badge
                        color="error"
                        variant="dot"
                        invisible={!(
                            (item.badge === 'draftInvoices' && draftInvoicesBadge > 0)
                            || (item.badge === 'draftReceipts' && draftReceiptsBadge > 0)
                            || (item.badge === 'pendingTasks' && pendingTasksBadge > 0)
                        )}
                        sx={{ '& .MuiBadge-badge': { top: 2, right: 2 } }}
                    >
                        {React.cloneElement(item.icon, { sx: { fontSize: railMode ? 22 : 19 } })}
                    </Badge>
                </ListItemIcon>
                {!railMode && (
                    <ListItemText
                        primary={renderNavLabel(item)}
                        primaryTypographyProps={{
                            fontSize: '0.8rem',
                            fontWeight: isActive ? 700 : 500,
                            component: 'div',
                            sx: { overflow: 'visible' },
                        }}
                        sx={{ my: 0, minWidth: 0, flex: '1 1 auto' }}
                    />
                )}
            </ListItemButton>
        );

        return (
            <ListItem key={`${item.path}-${item.text}`} disablePadding sx={{ mb: 0.2, px: railMode ? 0.75 : 0 }}>
                {railMode ? (
                    <Tooltip title={item.text} placement="right" arrow enterDelay={200}>
                        <Box sx={{ width: '100%' }}>{button}</Box>
                    </Tooltip>
                ) : button}
            </ListItem>
        );
    };

    const drawer = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: C.sidebar }}>
            {/* Brand header — Oracle-scale logo */}
            <Box
                sx={{
                    px: railMode ? 1 : 2,
                    py: railMode ? 1.5 : 2,
                    bgcolor: C.sidebarHeader,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: railMode ? 'center' : 'space-between',
                    minHeight: railMode ? 88 : 108,
                    gap: 1,
                    transition: 'min-height 200ms ease, padding 200ms ease',
                }}
            >
                {railMode ? (
                    <Tooltip title="Expand menu" placement="right">
                        <IconButton onClick={handleDrawerToggle} aria-label="Expand sidebar" sx={{ p: 0.5 }}>
                            <BrandLogo height={44} onDark compact />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <>
                        <Box sx={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
                            <BrandLogo height={64} onDark />
                            <Typography
                                variant="caption"
                                sx={{ opacity: 0.75, display: 'block', mt: 1.25, fontSize: '0.7rem', letterSpacing: 0.3 }}
                                noWrap
                            >
                                {business?.name || company?.name || distributor?.name || 'Enterprise ERP'}
                                {user?.role ? ` · ${user.role}` : ''}
                            </Typography>
                        </Box>
                        {!isMobile && (
                            <IconButton
                                size="small"
                                onClick={handleDrawerToggle}
                                aria-label="Collapse sidebar"
                                sx={{ color: '#fff', opacity: 0.75, flexShrink: 0, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.08)' } }}
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                        )}
                    </>
                )}
            </Box>

            {/* Navigation */}
            <List
                sx={{
                    flex: 1,
                    pt: railMode ? 1 : 0.75,
                    pb: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    px: railMode ? 0.5 : 1,
                    '&::-webkit-scrollbar': { width: 4 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: C.border, borderRadius: 4 },
                }}
            >
                {navigationMenu.map((category) => {
                    const hasActiveItem = category.items.some((item) => isItemActive(item.path));
                    const isExpanded = isCategoryExpanded(category.category, hasActiveItem);
                    const sectionBadge = category.items.reduce((n, item) => {
                        if (item.badge === 'draftInvoices') return n + draftInvoicesBadge;
                        if (item.badge === 'draftReceipts') return n + draftReceiptsBadge;
                        if (item.badge === 'pendingTasks') return n + pendingTasksBadge;
                        return n;
                    }, 0);

                    if (railMode) {
                        const open = railFlyout?.category === category.category;
                        return (
                            <Box key={category.category} sx={{ mb: 0.35, px: 0.75 }}>
                                <Tooltip title={category.category} placement="right" arrow enterDelay={300} disableHoverListener={open}>
                                    <ListItemButton
                                        onClick={(e) => {
                                            setRailFlyout((prev) => (
                                                prev?.category === category.category
                                                    ? null
                                                    : { category: category.category, anchorEl: e.currentTarget, items: category.items }
                                            ));
                                        }}
                                        aria-label={category.category}
                                        aria-haspopup="menu"
                                        aria-expanded={open}
                                        sx={{
                                            borderRadius: 1.5,
                                            py: 1.15,
                                            px: 1,
                                            minHeight: 46,
                                            justifyContent: 'center',
                                            bgcolor: hasActiveItem || open ? C.active : 'transparent',
                                            color: hasActiveItem || open ? C.primary : C.text,
                                            '&:hover': { bgcolor: C.hover },
                                        }}
                                    >
                                        <Badge
                                            color="error"
                                            badgeContent={sectionBadge > 0 ? sectionBadge : 0}
                                            invisible={!sectionBadge}
                                            sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 14, height: 14 } }}
                                        >
                                            {React.cloneElement(category.icon, {
                                                sx: { fontSize: 22, color: hasActiveItem || open ? C.primary : C.textSec },
                                            })}
                                        </Badge>
                                    </ListItemButton>
                                </Tooltip>
                            </Box>
                        );
                    }

                    return (
                        <Box key={category.category} sx={{ mb: 0.35 }}>
                            <ListItem disablePadding sx={{ mb: 0.15 }}>
                                <ListItemButton
                                    onClick={() => handleCategoryToggle(category.category, hasActiveItem)}
                                    aria-expanded={isExpanded}
                                    sx={{
                                        borderRadius: 1.5,
                                        py: 0.55,
                                        bgcolor: hasActiveItem ? C.active : 'transparent',
                                        '&:hover': { bgcolor: C.hover },
                                    }}
                                >
                                    <ListItemIcon sx={{ color: hasActiveItem ? C.primary : C.textSec, minWidth: 34 }}>
                                        {React.cloneElement(category.icon, { sx: { fontSize: 18 } })}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={category.category}
                                        primaryTypographyProps={{
                                            fontSize: '0.72rem',
                                            fontWeight: 800,
                                            letterSpacing: 0.4,
                                            textTransform: 'uppercase',
                                            color: hasActiveItem ? C.primary : C.textSec,
                                        }}
                                    />
                                    {isExpanded
                                        ? <ExpandLess sx={{ fontSize: 16, color: C.textSec }} />
                                        : <ExpandMore sx={{ fontSize: 16, color: C.textSec }} />}
                                </ListItemButton>
                            </ListItem>
                            <Collapse in={isExpanded} timeout={180} unmountOnExit>
                                <List component="div" disablePadding sx={{ pl: 0.5 }}>
                                    {category.items.map((item) => renderNavItem(item, { dense: true }))}
                                </List>
                            </Collapse>
                        </Box>
                    );
                })}
            </List>

            {!railMode && (
                <>
                    <Divider sx={{ borderColor: C.border }} />
                    {['Owner', 'CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin', 'SaaSAdmin'].includes(user?.role) ? (
                        <Box
                            component={RouterLink}
                            to="/settings/profile"
                            onClick={closeMobileDrawer}
                            sx={{
                                px: 2,
                                py: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                textDecoration: 'none',
                                color: 'inherit',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            <Avatar
                                src={resolveMediaUrl(user?.profilePicture) || undefined}
                                sx={{ width: 28, height: 28, bgcolor: C.primary, fontSize: '0.7rem' }}
                            >
                                {user?.firstName?.[0]}{user?.lastName?.[0]}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color={C.textSec} sx={{ fontSize: '0.7rem', fontWeight: 600 }} noWrap>
                                    {formatUserLabel(user, { withRole: false })}
                                </Typography>
                                <Typography variant="caption" color={C.textSec} sx={{ fontSize: '0.65rem', display: 'block' }} noWrap>
                                    {formatUserMeta(user) || user?.role}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                                src={resolveMediaUrl(user?.profilePicture) || undefined}
                                sx={{ width: 28, height: 28, bgcolor: C.primary, fontSize: '0.7rem' }}
                            >
                                {user?.firstName?.[0]}{user?.lastName?.[0]}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color={C.textSec} sx={{ fontSize: '0.7rem', fontWeight: 600 }} noWrap>
                                    {formatUserLabel(user, { withRole: false })}
                                </Typography>
                                <Typography variant="caption" color={C.textSec} sx={{ fontSize: '0.65rem', display: 'block' }} noWrap>
                                    {formatUserMeta(user) || user?.role}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {railMode && (
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Expand menu" placement="right">
                        <IconButton
                            size="small"
                            onClick={handleDrawerToggle}
                            aria-label="Expand sidebar"
                            sx={{ color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 1.5 }}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: C.contentBg }}>
            {/* App Bar */}
            <AppBar position="fixed" elevation={0} sx={{
                width: { md: `calc(100% - ${currentDrawerWidth}px)` },
                ml: { md: `${currentDrawerWidth}px` },
                bgcolor: '#FFFFFF', borderBottom: `1px solid ${C.border}`,
                transition: theme.transitions.create(['width', 'margin'], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            }}>
                <Toolbar sx={{ minHeight: { xs: 68, sm: 76 }, px: { xs: 1, sm: 2 }, gap: 0.5 }}>
                    <IconButton
                        edge="start"
                        onClick={handleDrawerToggle}
                        aria-label={isMobile ? 'Open menu' : (desktopExpanded ? 'Collapse menu' : 'Expand menu')}
                        sx={{ mr: { xs: 0.5, sm: 1.25 }, color: C.text }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography
                        component="div"
                        variant="body1"
                        noWrap
                        sx={{ flexGrow: 1, color: C.text, fontWeight: 700, letterSpacing: 0.2, minWidth: 0, overflow: 'hidden' }}
                    >
                        {!isOnline ? (
                            <Box
                                component="span"
                                onClick={() => navigate('/offline-queue')}
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: { xs: 1, sm: 1.5 }, py: 0.3, borderRadius: 999, bgcolor: '#fdecea', color: '#b71c1c', fontSize: { xs: '0.7rem', sm: '0.78rem' }, fontWeight: 600, maxWidth: '100%', cursor: 'pointer' }}
                                title="Open offline queue"
                            >
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#d32f2f', flexShrink: 0 }} />
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Offline {(pendingCount + failedCount) > 0 && `· ${pendingCount + failedCount}`}
                                </Box>
                            </Box>
                        ) : (pendingCount > 0 || failedCount > 0 || isSyncing) ? (
                            <Box
                                component="span"
                                onClick={() => navigate('/offline-queue')}
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: { xs: 1, sm: 1.5 }, py: 0.3, borderRadius: 999, bgcolor: failedCount ? '#fff3e0' : '#e8f5e9', color: failedCount ? '#e65100' : '#1b5e20', fontSize: { xs: '0.7rem', sm: '0.78rem' }, fontWeight: 600, maxWidth: '100%', cursor: 'pointer' }}
                                title="Open offline queue"
                            >
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: failedCount ? '#ef6c00' : C.primary, flexShrink: 0 }} />
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {failedCount > 0 ? `Sync issues · ${failedCount}` : `Syncing ${pendingCount}`}
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: { xs: 200, sm: 280, md: 340 } }}>
                                <BrandLogo height={48} />
                            </Box>
                        )}
                    </Typography>

                    <GlobalSearchBar />

                    <WorkingDistributorChip />

                    <NotificationBell />

                    <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="User menu" sx={{ color: C.text, ml: 0.25 }}>
                        <Avatar
                            src={resolveMediaUrl(user?.profilePicture) || undefined}
                            sx={{ bgcolor: C.primary, width: { xs: 30, sm: 34 }, height: { xs: 30, sm: 34 }, fontSize: '0.82rem' }}
                        >
                            {user?.firstName?.[0]}{user?.lastName?.[0]}
                        </Avatar>
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                        <Box sx={{ px: 2, py: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {formatUserLabel(user, { withRole: false })}
                            </Typography>
                            <Typography variant="caption" color={C.textSec}>
                                {formatUserMeta(user) || user?.role}
                            </Typography>
                        </Box>
                        <Divider />
                        {['Owner', 'CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin', 'SaaSAdmin'].includes(user?.role) && (
                        <MenuItem
                            component={RouterLink}
                            to="/settings/profile"
                            onClick={() => setAnchorEl(null)}
                        >
                            <ListItemIcon><PersonIcon fontSize="small" sx={{ color: C.primary }} /></ListItemIcon>My Profile
                        </MenuItem>
                        )}
                        <MenuItem onClick={() => { logout(); navigate('/login'); }}>
                            <ListItemIcon><Logout fontSize="small" sx={{ color: C.primary }} /></ListItemIcon>Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Sidebar */}
            <Box
                component="nav"
                sx={{
                    width: { md: currentDrawerWidth },
                    flexShrink: { md: 0 },
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    open
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            width: currentDrawerWidth,
                            boxSizing: 'border-box',
                            borderRight: `1px solid ${C.border}`,
                            overflowX: 'hidden',
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        },
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Main Content */}
            <Box component="main" sx={{
                flexGrow: 1,
                minWidth: 0,
                maxWidth: '100%',
                overflowX: 'clip',
                p: { xs: 1.5, sm: 2, md: 2.5 },
                width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
                mt: { xs: '68px', sm: '76px' },
                bgcolor: C.contentBg,
                minHeight: '100vh',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            }}>
                {user?.role !== 'SaaSAdmin' && <TrialBanner />}
                {pendingClosing && !location.pathname.startsWith('/operations/daily-closing') && (
                    <Box
                        sx={{
                            mb: 1.5, px: 1.5, py: 1, borderRadius: 1.5,
                            bgcolor: '#fff8e1',
                            color: '#92400e',
                            fontSize: '0.85rem', fontWeight: 600,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
                        }}
                    >
                        <span>
                            Previous day closing pending
                            {pendingClosing.dateLabel || pendingClosing.dateIso
                                ? ` — ${pendingClosing.dateLabel || pendingClosing.dateIso}`
                                : ''}
                            . You can keep working; finish that day before closing a newer one.
                        </span>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => {
                                const qs = pendingClosing.dateIso
                                    ? `?date=${encodeURIComponent(pendingClosing.dateIso)}`
                                    : '';
                                navigate(`/operations/daily-closing${qs}`);
                            }}
                            sx={{
                                border: '1px solid #d97706',
                                bgcolor: '#fff',
                                color: '#92400e',
                                borderRadius: 1,
                                px: 1.25,
                                py: 0.5,
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}
                        >
                            Open closing
                        </Box>
                    </Box>
                )}
                {storageWarning && (
                    <Box
                        onClick={() => navigate('/offline-queue')}
                        sx={{
                            mb: 1.5, px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer',
                            bgcolor: '#fff8e1',
                            color: '#e65100',
                            fontSize: '0.85rem', fontWeight: 600,
                        }}
                    >
                        {storageWarning}
                    </Box>
                )}
                <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                    <Outlet />
                </Box>
            </Box>
            <Suspense fallback={null}>
                <AISupportChat />
            </Suspense>

            {/* Collapsed rail: flyout with children of the clicked main section icon */}
            <Menu
                open={Boolean(railFlyout?.anchorEl)}
                anchorEl={railFlyout?.anchorEl || null}
                onClose={() => setRailFlyout(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            ml: 0.75,
                            minWidth: 200,
                            borderRadius: 2,
                            border: `1px solid ${C.border}`,
                            boxShadow: '0 8px 28px rgba(28,31,46,0.12)',
                        },
                    },
                }}
            >
                <Box sx={{ px: 1.75, pt: 1.25, pb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: 0.6, color: C.textSec, textTransform: 'uppercase' }}>
                        {railFlyout?.category}
                    </Typography>
                </Box>
                {(railFlyout?.items || []).map((item) => {
                    const isActive = isItemActive(item.path);
                    return (
                        <MenuItem
                            key={`${item.path}-${item.text}`}
                            component={RouterLink}
                            to={item.path}
                            onClick={() => setRailFlyout(null)}
                            selected={isActive}
                            sx={{
                                py: 1,
                                gap: 1,
                                fontSize: '0.85rem',
                                fontWeight: isActive ? 700 : 500,
                                color: isActive ? C.primary : C.text,
                                '&.Mui-selected': { bgcolor: C.active },
                                '&.Mui-selected:hover': { bgcolor: C.hover },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 32, color: isActive ? C.primary : C.textSec }}>
                                {React.cloneElement(item.icon, { sx: { fontSize: 18 } })}
                            </ListItemIcon>
                            {renderNavLabel(item)}
                        </MenuItem>
                    );
                })}
            </Menu>
        </Box>
    );
};

export default DashboardLayout;
